import { useState } from 'react'
import type { MetaFunction, ActionFunctionArgs, LoaderFunctionArgs } from 'react-router'
import { Form, useActionData, useLoaderData, useNavigation, redirect, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles } from '@afficheservices/db/schema'
import { eq, and, ilike, or, sql } from 'drizzle-orm'
import { register, createSessionCookie } from '~/lib/auth.server'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'
import { Building2, Search, MapPin, CheckCircle, ArrowRight, ArrowLeft, User, Mail, Lock, Phone, ShieldCheck, Gift } from 'lucide-react'

export const meta: MetaFunction = () => [
  { title: 'Revendiquez votre profil artisan — AfficheServices' },
  { name: 'description', content: 'Votre entreprise est déjà listée dans notre annuaire ? Revendiquez votre profil en quelques minutes pour recevoir des demandes de devis gratuites.' },
  { name: 'robots', content: 'noindex,follow' },
]

// ─── Loader: pre-fill SIRET from query string ──────────────────

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const siret = (url.searchParams.get('siret') ?? '').replace(/\s/g, '')
  const q = url.searchParams.get('q') ?? ''

  if (!siret && !q) return { matches: [], query: '' }

  // Lookup against directory (is_demo=true) profiles only. SIRET is exact,
  // name is ILIKE fuzzy + optional city fragment.
  const cond =
    siret.length === 14
      ? eq(professionalProfiles.siret, siret)
      : or(
          ilike(professionalProfiles.companyName, `%${q}%`),
          ilike(professionalProfiles.city, `%${q}%`),
        )

  const matches = await db
    .select({
      id: professionalProfiles.id,
      companyName: professionalProfiles.companyName,
      siret: professionalProfiles.siret,
      city: professionalProfiles.city,
      postalCode: professionalProfiles.postalCode,
      slug: professionalProfiles.slug,
      isDemo: professionalProfiles.isDemo,
    })
    .from(professionalProfiles)
    .where(and(cond!, eq(professionalProfiles.isDemo, true)))
    .limit(10)

  return { matches, query: siret || q }
}

// ─── Action: create account + reassign profile ────────────────

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'auth')) return rateLimitResponse()

  const formData = await request.formData()
  const profileId = formData.get('profileId') as string
  const firstName = (formData.get('firstName') as string)?.trim()
  const lastName = (formData.get('lastName') as string)?.trim()
  const email = (formData.get('email') as string)?.trim().toLowerCase()
  const password = formData.get('password') as string
  const phone = (formData.get('phone') as string)?.trim()

  const errors: Record<string, string> = {}
  if (!profileId) errors.form = 'Profil introuvable. Relancez la recherche.'
  if (!firstName) errors.firstName = 'Requis'
  if (!lastName) errors.lastName = 'Requis'
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errors.email = 'Email invalide'
  if (!password || password.length < 8) errors.password = 'Min. 8 caractères'
  if (!phone) errors.phone = 'Requis'

  if (Object.keys(errors).length > 0) return { errors }

  // Verify the profile still exists and is still claimable
  const [profile] = await db
    .select({
      id: professionalProfiles.id,
      userId: professionalProfiles.userId,
      companyName: professionalProfiles.companyName,
      isDemo: professionalProfiles.isDemo,
    })
    .from(professionalProfiles)
    .where(and(eq(professionalProfiles.id, profileId), eq(professionalProfiles.isDemo, true)))
    .limit(1)

  if (!profile) {
    return { errors: { form: 'Ce profil a déjà été revendiqué ou n\'existe plus.' } }
  }

  // Create a new user for the claiming artisan
  const registered = await register({ email, password, firstName, lastName, phone, role: 'professional' })
  if ('error' in registered) return { errors: { email: registered.error } }

  const newUserId = registered.userId!
  const oldUserId = profile.userId

  // Reassign the profile to the new user and flip is_demo off.
  // `user_id` is UNIQUE on professional_profiles; the new user has no
  // existing profile so this UPDATE cannot conflict.
  await db
    .update(professionalProfiles)
    .set({ userId: newUserId, isDemo: false, identityVerified: true, updatedAt: new Date() })
    .where(eq(professionalProfiles.id, profile.id))

  // Remove the stub directory user — they're no longer referenced.
  // Only delete if it looks like one of our placeholder users.
  await db.execute(sql`
    DELETE FROM users
    WHERE id = ${oldUserId}
      AND password_hash = 'nologin-directory-import'
  `)

  const cookie = await createSessionCookie(newUserId, request)
  return redirect('/espace-pro?claimed=1', { headers: { 'Set-Cookie': cookie } })
}

// ─── View ──────────────────────────────────────────────────────

const INPUT = 'block w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
const ERR = 'mt-1 text-xs text-red-500'

export default function RevendiquerPage() {
  const { matches, query } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'
  const errors = actionData?.errors as Record<string, string> | undefined

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedProfile = matches.find((m) => m.id === selectedId) ?? null

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto max-w-2xl px-4">
        {/* Hero */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700">
            <Building2 className="h-3.5 w-3.5" />
            Revendiquer un profil
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Votre entreprise est déjà dans notre annuaire ?
          </h1>
          <p className="mt-3 text-base text-slate-600">
            Reprenez le contrôle de votre profil. 100% gratuit. 2 minutes.
          </p>
        </div>

        {/* Trust strip — security/data/cost signals above the form */}
        <aside
          aria-label="Garanties de sécurité et de confiance"
          className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-900"
        >
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            <span>Données SIRENE/INSEE — vérification automatique</span>
          </span>
          <span className="hidden h-3 w-px bg-emerald-300/50 sm:block" aria-hidden="true" />
          <span className="flex items-center gap-1.5">
            <Lock className="h-4 w-4 text-blue-600" aria-hidden="true" />
            <span>Chiffré SSL</span>
          </span>
          <span className="hidden h-3 w-px bg-emerald-300/50 sm:block" aria-hidden="true" />
          <span className="flex items-center gap-1.5">
            <Gift className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            <span>100% gratuit, aucun engagement</span>
          </span>
        </aside>

        {/* Step 1: Search */}
        {!selectedProfile && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Search className="h-5 w-5 text-primary" /> Trouvez votre entreprise
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Saisissez votre numéro SIRET (14 chiffres) ou le nom de votre entreprise.
            </p>

            <Form method="get" className="mt-4 flex gap-2">
              <input
                type="text"
                name="q"
                defaultValue={query}
                placeholder="SIRET ou nom d'entreprise"
                className={INPUT}
                autoFocus
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white hover:bg-primary-hover"
              >
                Rechercher <ArrowRight className="h-4 w-4" />
              </button>
            </Form>

            {/* Results */}
            {query && matches.length === 0 && (
              <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Aucune entreprise trouvée pour « {query} ». Vérifiez l'orthographe ou{' '}
                <Link to="/register-pro" className="font-semibold underline">
                  créez un nouveau profil
                </Link>
                .
              </div>
            )}

            {matches.length > 0 && (
              <div className="mt-6 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {matches.length} résultat{matches.length > 1 ? 's' : ''}
                </p>
                {matches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className="flex w-full items-start justify-between gap-3 rounded-xl border border-border bg-slate-50 p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">{m.companyName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {m.city} {m.postalCode}
                        </span>
                        <span className="font-mono">SIRET {m.siret}</span>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Confirm + Register */}
        {selectedProfile && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <button
              onClick={() => setSelectedId(null)}
              className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Retour aux résultats
            </button>

            <div className="rounded-xl border-2 border-primary bg-primary/5 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                <CheckCircle className="h-4 w-4" /> Profil sélectionné
              </div>
              <p className="mt-2 text-base font-bold text-slate-900">{selectedProfile.companyName}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {selectedProfile.city} {selectedProfile.postalCode}
                </span>
                <span className="font-mono">SIRET {selectedProfile.siret}</span>
              </div>
              <Link
                to={`/pro/${selectedProfile.slug}`}
                target="_blank"
                className="mt-2 inline-block text-xs text-primary underline"
              >
                Voir le profil actuel
              </Link>
            </div>

            <h2 className="mt-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
              <User className="h-5 w-5 text-primary" /> Créez votre compte
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Confirmez votre identité pour prendre le contrôle de ce profil.
            </p>

            <Form method="post" className="mt-4 space-y-4">
              <input type="hidden" name="profileId" value={selectedProfile.id} />

              {errors?.form && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  {errors.form}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">Prénom</label>
                  <input name="firstName" type="text" className={INPUT} />
                  {errors?.firstName && <p className={ERR}>{errors.firstName}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">Nom</label>
                  <input name="lastName" type="text" className={INPUT} />
                  {errors?.lastName && <p className={ERR}>{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">
                  <Mail className="mr-1 inline h-3 w-3" /> Email professionnel
                </label>
                <input name="email" type="email" className={INPUT} />
                {errors?.email && <p className={ERR}>{errors.email}</p>}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">
                  <Lock className="mr-1 inline h-3 w-3" /> Mot de passe (min. 8 caractères)
                </label>
                <input name="password" type="password" className={INPUT} />
                {errors?.password && <p className={ERR}>{errors.password}</p>}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">
                  <Phone className="mr-1 inline h-3 w-3" /> Téléphone
                </label>
                <input name="phone" type="tel" className={INPUT} />
                {errors?.phone && <p className={ERR}>{errors.phone}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
              >
                {isSubmitting ? 'Revendication en cours…' : 'Revendiquer mon profil'}
                <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-center text-xs text-slate-400">
                En revendiquant ce profil vous acceptez nos{' '}
                <Link to="/mentions-legales" className="underline">CGU</Link>.
              </p>
            </Form>
          </div>
        )}

        {/* Fallback: no match / not in directory */}
        {!selectedProfile && (
          <div className="mt-6 text-center text-xs text-slate-500">
            Votre entreprise n'est pas dans notre annuaire ?{' '}
            <Link to="/register-pro" className="font-semibold text-primary underline">
              Créez un nouveau profil
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
